import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarWidget } from "@/components/ui/calendar";
import { toast } from "sonner";
import axios from "axios";
import { 
  Zap, Plus, Calendar, MapPin, Users, Eye, Archive, LogOut, 
  BarChart3, Copy, Power, GitCompare, Filter, Trash2, Search, Pencil, CalendarIcon
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [filter, setFilter] = useState("semester");
  const [searchQuery, setSearchQuery] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [createDate, setCreateDate] = useState(undefined);
  const [editDate, setEditDate] = useState(undefined);
  const [newEvent, setNewEvent] = useState({
    name: "",
    description: "",
    date: "",
    location: "",
  });

  const formatDateDisplay = (date) => {
    if (!date) return "";
    const d = String(date.getDate()).padStart(2, "0");
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const y = date.getFullYear();
    return `${d}/${m}/${y}`;
  };

  const toIsoDate = (date) => {
    if (!date) return null;
    const d = String(date.getDate()).padStart(2, "0");
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const y = date.getFullYear();
    return `${y}-${m}-${d}`;
  };

  const parseIsoToDate = (isoStr) => {
    if (!isoStr) return undefined;
    const [y, m, d] = isoStr.split("-").map(Number);
    return new Date(y, m - 1, d);
  };

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
  });

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin");
      return;
    }
    fetchEvents();
  }, [navigate, filter]);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events?filter=${filter}`, getAuthHeaders());
      setEvents(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin");
      }
      toast.error("Failed to fetch events");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    const isoDate = toIsoDate(createDate);
    if (!newEvent.name || !isoDate) {
      toast.error("Please fill in required fields");
      return;
    }

    try {
      await axios.post(`${API}/events`, { ...newEvent, date: isoDate }, getAuthHeaders());
      toast.success("Event created successfully");
      setCreateDialogOpen(false);
      setNewEvent({ name: "", description: "", date: "", location: "" });
      setCreateDate(undefined);
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create event");
    }
  };

  const handleToggleEvent = async (eventId) => {
    try {
      const response = await axios.patch(`${API}/events/${eventId}/toggle`, {}, getAuthHeaders());
      toast.success(response.data.is_active ? "Event activated" : "Event deactivated");
      fetchEvents();
    } catch (error) {
      toast.error("Failed to toggle event");
    }
  };

  const handleArchiveEvent = async (eventId) => {
    try {
      const response = await axios.patch(`${API}/events/${eventId}/archive`, {}, getAuthHeaders());
      toast.success(response.data.is_archived ? "Event archived" : "Event unarchived");
      fetchEvents();
    } catch (error) {
      toast.error("Failed to archive event");
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm("Permanently delete this event and all its responses? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/events/${eventId}`, getAuthHeaders());
      toast.success("Event deleted permanently");
      fetchEvents();
    } catch (error) {
      toast.error("Failed to delete event");
    }
  };

  const openEditDialog = (event) => {
    setEditingEvent({
      id: event.id,
      name: event.name,
      description: event.description || "",
      date: event.date,
      location: event.location || "",
    });
    setEditDate(parseIsoToDate(event.date));
    setEditDialogOpen(true);
  };

  const handleEditEvent = async (e) => {
    e.preventDefault();
    const isoDate = toIsoDate(editDate);
    if (!editingEvent.name || !isoDate) {
      toast.error("Please fill in required fields");
      return;
    }
    try {
      await axios.put(`${API}/events/${editingEvent.id}`, {
        name: editingEvent.name,
        description: editingEvent.description,
        date: isoDate,
        location: editingEvent.location,
      }, getAuthHeaders());
      toast.success("Event updated successfully");
      setEditDialogOpen(false);
      setEditingEvent(null);
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update event");
    }
  };

  const copyEventCode = (code) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(code).then(() => toast.success("Event code copied!")).catch(() => {
        fallbackCopy(code);
      });
    } else {
      fallbackCopy(code);
    }
  };
  const fallbackCopy = (code) => {
    const ta = document.createElement("textarea");
    ta.value = code;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
    toast.success("Event code copied!");
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_username");
    navigate("/admin");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      {/* Sidebar */}
      <div className="fixed left-0 top-0 h-full w-64 bg-zinc-900/50 border-r border-zinc-800 p-6 hidden lg:block">
        <div className="flex items-center gap-3 mb-10">
          <div className="w-10 h-10 rounded-lg bg-orange-500 flex items-center justify-center">
            <Zap className="w-5 h-5" />
          </div>
          <div>
            <p className="font-heading text-sm tracking-wide">TEAMPOWER</p>
            <p className="text-xs text-zinc-500">Admin</p>
          </div>
        </div>

        <nav className="space-y-2">
          <button 
            className="sidebar-link active w-full"
            data-testid="nav-events"
          >
            <Calendar className="w-5 h-5" />
            Events
          </button>
          <button 
            onClick={() => navigate("/admin/reports")}
            className="sidebar-link w-full"
            data-testid="nav-reports"
          >
            <BarChart3 className="w-5 h-5" />
            Reports
          </button>
          <button 
            onClick={() => navigate("/admin/comparison")}
            className="sidebar-link w-full"
            data-testid="nav-comparison"
          >
            <GitCompare className="w-5 h-5" />
            Team Comparison
          </button>
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start text-zinc-400 hover:text-white"
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Mobile Header */}
        <header className="lg:hidden flex items-center justify-between p-4 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-orange-500 flex items-center justify-center">
              <Zap className="w-4 h-4" />
            </div>
            <span className="font-heading text-sm">TEAMPOWER</span>
          </div>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={() => navigate("/admin/dashboard")} className="text-zinc-400 hover:text-white" title="Events">
              <Calendar className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate("/admin/reports")} className="text-zinc-400 hover:text-white" title="Reports">
              <BarChart3 className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate("/admin/comparison")} className="text-zinc-400 hover:text-white" title="Team Comparison">
              <GitCompare className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-zinc-400 hover:text-white" title="Logout">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </header>

        <main className="p-6 md:p-8">
          {/* Page Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="font-heading text-3xl tracking-wide">EVENTS</h1>
              <p className="text-zinc-500">Manage your teambuilding events</p>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              {/* Search Bar */}
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search events..."
                  className="pl-9 w-56 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                  data-testid="search-events-input"
                />
              </div>

              {/* Filter Dropdown */}
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-44 bg-zinc-800 border-zinc-700 text-white" data-testid="filter-select">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectItem value="semester">This Semester</SelectItem>
                  <SelectItem value="year">This Year</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                  <SelectItem value="all">All Events</SelectItem>
                </SelectContent>
              </Select>

              <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-orange-500 hover:bg-orange-600 font-bold glow-orange glow-orange-hover" data-testid="create-event-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Event
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-xl">Create New Event</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateEvent} className="space-y-4" data-testid="create-event-form">
                    <div>
                      <Label className="text-zinc-400">Event Name *</Label>
                      <Input
                        value={newEvent.name}
                        onChange={(e) => setNewEvent({ ...newEvent, name: e.target.value })}
                        className="mt-2 bg-zinc-800 border-zinc-700"
                        placeholder="Q1 Team Meeting"
                        data-testid="event-name-input"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400">Date *</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full mt-2 justify-start text-left bg-zinc-800 border-zinc-700 hover:bg-zinc-700 text-white"
                            data-testid="event-date-input"
                          >
                            <CalendarIcon className="w-4 h-4 mr-2 text-zinc-400" />
                            {createDate ? formatDateDisplay(createDate) : <span className="text-zinc-500">DD/MM/YYYY</span>}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-700" align="start">
                          <CalendarWidget
                            mode="single"
                            selected={createDate}
                            onSelect={setCreateDate}
                            className="text-white"
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div>
                      <Label className="text-zinc-400">Location</Label>
                      <Input
                        value={newEvent.location}
                        onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
                        className="mt-2 bg-zinc-800 border-zinc-700"
                        placeholder="Athens, Greece"
                        data-testid="event-location-input"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400">Description</Label>
                      <Input
                        value={newEvent.description}
                        onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                        className="mt-2 bg-zinc-800 border-zinc-700"
                        placeholder="Optional description"
                        data-testid="event-description-input"
                      />
                    </div>
                    <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600 font-bold" data-testid="submit-create-event">
                      Create Event
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Events Grid */}
          {(() => {
            const filtered = searchQuery.trim()
              ? events.filter(e => e.name.toLowerCase().includes(searchQuery.toLowerCase()) || e.code.toLowerCase().includes(searchQuery.toLowerCase()) || (e.location && e.location.toLowerCase().includes(searchQuery.toLowerCase())))
              : events;
            return filtered.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-20"
            >
              <Calendar className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
              <h3 className="text-xl text-zinc-400 mb-2">
                {searchQuery ? "No matching events" : filter === "archived" ? "No archived events" : "No events yet"}
              </h3>
              <p className="text-zinc-500">
                {searchQuery
                  ? "Try a different search term"
                  : filter === "archived" 
                    ? "Archived events will appear here" 
                    : "Create your first event to get started"}
              </p>
            </motion.div>
          ) : (
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
              {filtered.map((event, index) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`bg-zinc-900/50 border ${event.is_archived ? 'border-zinc-700/50' : event.is_active ? 'border-zinc-800' : 'border-zinc-800/50'} rounded-xl p-6 ${event.is_archived ? 'opacity-70' : !event.is_active ? 'opacity-60' : ''}`}
                  data-testid={`event-card-${event.id}`}
                >
                  {/* Event Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-heading text-lg">{event.name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <button
                          onClick={() => copyEventCode(event.code)}
                          className="flex items-center gap-1 text-orange-400 hover:text-orange-300 text-sm"
                          data-testid={`copy-code-${event.id}`}
                        >
                          <Copy className="w-3 h-3" />
                          {event.code}
                        </button>
                      </div>
                    </div>
                    <div className={`px-2 py-1 rounded text-xs ${
                      event.is_archived 
                        ? 'bg-zinc-700/50 text-zinc-400' 
                        : event.is_active 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-zinc-700/50 text-zinc-400'
                    }`}>
                      {event.is_archived ? 'Archived' : event.is_active ? 'Active' : 'Inactive'}
                    </div>
                  </div>

                  {/* Event Details */}
                  <div className="space-y-2 text-sm text-zinc-400 mb-4">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {event.date}
                    </div>
                    {event.location && (
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        {event.location}
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      {event.response_count} responses
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 pt-4 border-t border-zinc-800">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => navigate(`/admin/events/${event.id}`)}
                      className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-white"
                      data-testid={`view-event-${event.id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View
                    </Button>
                    {!event.is_archived && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(event)}
                        className="text-zinc-400 hover:text-white"
                        data-testid={`edit-event-${event.id}`}
                        title="Edit event"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                    )}
                    {!event.is_archived && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleEvent(event.id)}
                        className="text-zinc-400 hover:text-white"
                        data-testid={`toggle-event-${event.id}`}
                      >
                        <Power className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleArchiveEvent(event.id)}
                      className={event.is_archived ? "text-orange-400 hover:text-orange-300" : "text-zinc-400 hover:text-white"}
                      data-testid={`archive-event-${event.id}`}
                      title={event.is_archived ? "Unarchive" : "Archive"}
                    >
                      <Archive className="w-4 h-4" />
                    </Button>
                    {event.is_archived && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteEvent(event.id)}
                        className="text-red-400 hover:text-red-300"
                        data-testid={`delete-event-${event.id}`}
                        title="Delete permanently"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
          )()}

          {/* Edit Event Dialog */}
          <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
            <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Edit Event</DialogTitle>
              </DialogHeader>
              {editingEvent && (
                <form onSubmit={handleEditEvent} className="space-y-4" data-testid="edit-event-form">
                  <div>
                    <Label className="text-zinc-400">Event Name *</Label>
                    <Input
                      value={editingEvent.name}
                      onChange={(e) => setEditingEvent({ ...editingEvent, name: e.target.value })}
                      className="mt-2 bg-zinc-800 border-zinc-700"
                      data-testid="edit-event-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400">Date *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full mt-2 justify-start text-left bg-zinc-800 border-zinc-700 hover:bg-zinc-700 text-white"
                          data-testid="edit-event-date-input"
                        >
                          <CalendarIcon className="w-4 h-4 mr-2 text-zinc-400" />
                          {editDate ? formatDateDisplay(editDate) : <span className="text-zinc-500">DD/MM/YYYY</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-700" align="start">
                        <CalendarWidget
                          mode="single"
                          selected={editDate}
                          onSelect={setEditDate}
                          className="text-white"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label className="text-zinc-400">Location</Label>
                    <Input
                      value={editingEvent.location}
                      onChange={(e) => setEditingEvent({ ...editingEvent, location: e.target.value })}
                      className="mt-2 bg-zinc-800 border-zinc-700"
                      data-testid="edit-event-location-input"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400">Description</Label>
                    <Input
                      value={editingEvent.description}
                      onChange={(e) => setEditingEvent({ ...editingEvent, description: e.target.value })}
                      className="mt-2 bg-zinc-800 border-zinc-700"
                      data-testid="edit-event-description-input"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600 font-bold" data-testid="submit-edit-event">
                    Save Changes
                  </Button>
                </form>
              )}
            </DialogContent>
          </Dialog>
        </main>
      </div>
    </div>
  );
}
